import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Chip,
    Menu,
    Button,
    Checkbox,
    Badge
} from "@mui/material";

import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from 'components/lib'

/* ============================= */
/* INTERNAL FILTER MENU COMPONENT */
/* ============================= */

function FilterMenu({
    label,
    icon,
    options = [],
    appliedValues = [],
    setAppliedValues,
    onOpen,
}) {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [localSelected, setLocalSelected] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const open = Boolean(anchorEl);

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const toggleOption = (value) => {
        setLocalSelected(prev => {
            const exists = prev.some(
                v => v.toLowerCase() === value.toLowerCase()
            );

            if (exists) {
                return prev.filter(
                    v => v.toLowerCase() !== value.toLowerCase()
                );
            }

            return [...prev, value];
        });
    };

    useEffect(() => {
        setLocalSelected([...appliedValues]);
    }, [appliedValues]);

    return (
        <>
            <Button
                onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setLocalSelected([...appliedValues]);
                    setSearchTerm("");
                    if (onOpen) onOpen();
                }}
                startIcon={icon}
                endIcon={
                    <KeyboardArrowDownIcon
                        sx={{
                            transition: "transform 0.2s ease",
                            transform: open ? "rotate(180deg)" : "rotate(0deg)"
                        }}
                    />
                }
                sx={{
                    borderRadius: "999px",
                    px: 2,
                    py: 0.7,
                    textTransform: "none",
                    fontWeight: 500,
                    border: "1px solid",
                    transition: "all 0.2s ease",
                    ...(appliedValues.length > 0
                        ? {
                            backgroundColor: "#e0f2fe",
                            borderColor: "#3b82f6",
                            color: "#1d4ed8"
                        }
                        : {
                            backgroundColor: "#f3f4f6",
                            borderColor: "#e5e7eb",
                            color: "#374151"
                        })
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {label}

                    {appliedValues.length > 0 && (
                        <Box
                            sx={{
                                backgroundColor: "#2563eb",
                                color: "#fff",
                                fontSize: 10,
                                fontWeight: 600,
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {appliedValues.length}
                        </Box>
                    )}
                </Box>
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => {
                setAnchorEl(null);
                setSearchTerm("");
            }}>
                <Box sx={{ p: 1 }}>
                    <input
                        type="text"
                        placeholder={t("Components.FilterBar.search")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "6px",
                            border: "1px solid #ddd",
                        }}
                    />
                </Box>

                <Box sx={{ maxHeight: 250, overflowY: "auto" }}>
                    {filteredOptions.map((option) => (
                        <Box
                            key={option.value}
                            onClick={() => toggleOption(option.value)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 2,
                                py: 1,
                                cursor: "pointer",
                                "&:hover": { background: "#f3f4f6" },
                            }}
                        >
                            <Checkbox checked={localSelected.some(
                                v => v.toLowerCase() === option.value.toLowerCase()
                            )} />
                            {option.label}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ p: 1, textAlign: "right" }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            setAppliedValues([...localSelected]);
                            setAnchorEl(null);
                            setSearchTerm("");
                        }}
                    >
                        {t("Components.FilterBar.apply")}
                    </Button>
                </Box>
            </Menu>
        </>
    );
}

/* ============================= */
/* MAIN FILTER BAR COMPONENT */
/* ============================= */

export default function FilterBar({
    typeOptions = [],
    tagOptions = [],
    appliedTypes = [],
    setAppliedTypes,
    appliedTags = [],
    setAppliedTags,
    quickTags = [],
    onFetchTags,
}) {
    const { t } = useTranslation();
    const hasFilters = appliedTypes.length > 0 || appliedTags.length > 0;

    return (
        <>
            {/* TOP FILTER ROW */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: hasFilters ? 1 : 2,
                }}
            >
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                    {t("Components.FilterBar.filters")}:
                </Typography>

                <FilterMenu
                    label={t("Components.FilterBar.type")}
                    icon={<LayersOutlinedIcon fontSize="small" />}
                    appliedValues={appliedTypes}
                    setAppliedValues={setAppliedTypes}
                    options={typeOptions}
                />

                <Box sx={{ height: 20, width: "1px", backgroundColor: "#e5e7eb" }} />

                <FilterMenu
                    label={t("Components.FilterBar.tags")}
                    icon={<LocalOfferOutlinedIcon fontSize="small" />}
                    appliedValues={appliedTags}
                    setAppliedValues={setAppliedTags}
                    options={tagOptions}
                    onOpen={onFetchTags}
                />

                <Box sx={{ height: 20, width: "1px", backgroundColor: "#e5e7eb" }} />

                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                    {t("Components.FilterBar.quick_tags")}:
                </Typography>

                {quickTags.map(tag => {
                    const normalized = tag;
                    const isSelected = appliedTags.some(
                        t => t.toLowerCase() === normalized.toLowerCase()
                    );

                    return (
                        <Chip
                            key={tag}
                            label={tag}
                            clickable
                            onClick={() => {
                                setAppliedTags(prev => {
                                    const exists = prev.some(
                                        t => t.toLowerCase() === normalized.toLowerCase()
                                    );

                                    if (exists) {
                                        return prev.filter(
                                            t => t.toLowerCase() !== normalized.toLowerCase()
                                        );
                                    }

                                    return [...prev, normalized];
                                });
                            }}
                            sx={{
                                backgroundColor: isSelected ? "#dbeafe" : "#f3f4f6",
                                color: isSelected ? "#1e40af" : "#374151",
                                border: "1px solid",
                                borderColor: isSelected ? "#3b82f6" : "#e5e7eb",
                            }}
                        />
                    );
                })}
            </Box>

            {/* APPLIED FILTERS ROW */}
            {hasFilters && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>

                    {/* TYPE CHIPS */}
                    {appliedTypes.map(type => (
                        <Chip
                            key={type}
                            icon={<LayersOutlinedIcon fontSize="small" />}
                            label={
                                typeOptions.find((opt) => opt.value === type)?.label || type
                            }
                            onDelete={() =>
                                setAppliedTypes(prev => prev.filter(t => t !== type))
                            }
                            sx={{
                                backgroundColor: "#e0f2fe",
                                color: "#1d4ed8",
                                border: "1px solid #3b82f6",
                            }}
                        />
                    ))}

                    {/* TAG CHIPS */}
                    {appliedTags.map((tag) => (
                        <Chip
                            key={tag}
                            icon={<LocalOfferOutlinedIcon fontSize="small" />}
                            label={
                                tagOptions.find((opt) => opt.value === tag)?.label || tag
                            }
                            onDelete={() =>
                                setAppliedTags((prev) => prev.filter((t) => t !== tag))
                            }
                            sx={{
                                backgroundColor: "#f0fdf4",
                                color: "#166534",
                                border: "1px solid #22c55e",
                            }}
                        />
                    ))}

                    {/* CLEAR ALL */}
                    <Chip
                        icon={<CloseIcon />}
                        label={t("Components.FilterBar.clear_all")}
                        onClick={() => {
                            setAppliedTypes([]);
                            setAppliedTags([]);
                        }}
                        sx={{
                            backgroundColor: "#1450a0",
                            color: "#fff",
                            fontWeight: 500,
                            "& .MuiChip-icon": {
                                color: "#fff",
                            },
                            "&:hover": {
                                backgroundColor: "#1d4ed8",
                            },
                        }}
                    />
                </Box>
            )}
        </>
    );
}